import { useState, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Menu,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
  Tooltip,
  Typography
} from '@mui/material'
import {
  DateRange as DateRangeIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon
} from '@mui/icons-material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import fi from 'date-fns/locale/fi'
import {
  addDays,
  addMinutes,
  addMonths,
  addWeeks,
  differenceInMinutes,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isAfter,
  isBefore,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subWeeks
} from 'date-fns'
import TimePeriod from '../enums/TimePeriod'
import { RootState } from '../redux'
import { getInitialTimePeriod, setDateRange, setTimePeriod } from '../redux/slices/query'
import { DateRange } from '../types'

/**
 * A customized toggle button that allows itself to wrapped inside a tooltip.
 *
 * @see https://github.com/mui/material-ui/issues/18091
 */
/* const ToggleButtonInsideTooltip = forwardRef((props: ToggleButtonProps) => {
  return (
    <>
      <ToggleButton ref={props?.innerRef} {...props} />
    </>
  )
}) */

export interface DateRangeVisibilityOptions {
  includeOneHourOption?: boolean
  includeTwentyFourHoursOption?: boolean
  includeThreeDaysOption?: boolean
  includeSevenDaysOption?: boolean
  includeThirtyDaysOption?: boolean
  showRelativeTimePeriodOptions?: boolean
  showAbsoluteTimePeriodOptions?: boolean
}

type RelativeTimePeriodToggleButtonGroupProps = Omit<ToggleButtonGroupProps, 'exclusive'> &
  Pick<
    DateRangeVisibilityOptions,
    | 'includeOneHourOption'
    | 'includeTwentyFourHoursOption'
    | 'includeThreeDaysOption'
    | 'includeSevenDaysOption'
    | 'includeThirtyDaysOption'
  > & {
    shrink?: boolean
  }

function RelativeTimePeriodToggleButtonGroup({
  value,
  color = 'primary',
  children,
  includeOneHourOption = false,
  includeTwentyFourHoursOption = true,
  includeThreeDaysOption = false,
  includeSevenDaysOption = true,
  includeThirtyDaysOption = true,
  shrink = false,
  onChange,
  ...props
}: RelativeTimePeriodToggleButtonGroupProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  return (
    <ToggleButtonGroup {...props} color={color} value={value} exclusive onChange={onChange}>
      {includeOneHourOption && (
        <Tooltip title={t('showDataForTheLastHour') as string}>
          <ToggleButton value={TimePeriod.ONE_HOUR} selected={value === TimePeriod.ONE_HOUR}>
            <Typography>{shrink ? '1h' : t('TimePeriod.1h')}</Typography>
          </ToggleButton>
        </Tooltip>
      )}

      {includeTwentyFourHoursOption && (
        <Tooltip title={t('showDataForTheLastTwentyFourHours') as string}>
          <ToggleButton
            value={TimePeriod.TWENTYFOUR_HOURS}
            selected={value === TimePeriod.TWENTYFOUR_HOURS}
          >
            {shrink ? '24h' : t('TimePeriod.24h')}
          </ToggleButton>
        </Tooltip>
      )}

      {includeThreeDaysOption && (
        <Tooltip title={t('showDataForTheLastThreeDays') as string}>
          <ToggleButton value={TimePeriod.THREE_DAYS} selected={value === TimePeriod.THREE_DAYS}>
            {shrink ? '3d' : t('TimePeriod.3d')}
          </ToggleButton>
        </Tooltip>
      )}

      {includeSevenDaysOption && (
        <Tooltip title={t('showDataForTheLastSevenDays') as string}>
          <ToggleButton value={TimePeriod.SEVEN_DAYS} selected={value === TimePeriod.SEVEN_DAYS}>
            {shrink ? '7d' : t('TimePeriod.7d')}
          </ToggleButton>
        </Tooltip>
      )}

      {includeThirtyDaysOption && (
        <Tooltip title={t('showDataForTheLastThirtyDays') as string}>
          <ToggleButton value={TimePeriod.THIRTY_DAYS} selected={value === TimePeriod.THIRTY_DAYS}>
            {shrink ? '30d' : t('TimePeriod.30d')}
          </ToggleButton>
        </Tooltip>
      )}

      {children}
    </ToggleButtonGroup>
  )
}

type AbosoluteTimePeriodToggleButtonGroupProps = Omit<ToggleButtonGroupProps, 'exclusive'>

function AbosoluteTimePeriodToggleButtonGroup({
  value,
  color = 'primary',
  children,
  onChange,
  ...props
}: AbosoluteTimePeriodToggleButtonGroupProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  return (
    <ToggleButtonGroup {...props} color={color} value={value} exclusive onChange={onChange}>
      <Tooltip title={t('showDataForTheDay') as string}>
        <ToggleButton value={TimePeriod.DAY} selected={value === TimePeriod.DAY}>
          {t('day')}
        </ToggleButton>
      </Tooltip>

      <Tooltip title={t('showDataForTheWeek') as string}>
        <ToggleButton value={TimePeriod.WEEK} selected={value === TimePeriod.WEEK}>
          {t('week')}
        </ToggleButton>
      </Tooltip>

      <Tooltip title={t('showDataForTheMonth') as string}>
        <ToggleButton value={TimePeriod.MONTH} selected={value === TimePeriod.MONTH}>
          {t('month')}
        </ToggleButton>
      </Tooltip>

      {children}
    </ToggleButtonGroup>
  )
}

export interface DateRangeSelectToolboxProps extends DateRangeVisibilityOptions {
  value: DateRange
  size?: 'small' | 'medium' | 'large' | undefined
  customControl?: ReactNode
  // onChange?: (value: DateRange) => void
  // onCancel?: () => void
}

interface CustomTimePeriodOptionProps extends Omit<ToggleButtonProps, 'children' | 'value'> {
  label?: string
}

function CustomTimePeriodOption({ label, onClick, ...props }: CustomTimePeriodOptionProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The selected time period.
   */
  const { timePeriod } = useSelector((state: RootState) => state.query)

  return (
    <Tooltip title={t('selectCustomDateRange') as string}>
      <ToggleButton
        value={TimePeriod.CUSTOM}
        selected={
          timePeriod === TimePeriod.CUSTOM ||
          timePeriod === TimePeriod.DAY ||
          timePeriod === TimePeriod.WEEK ||
          timePeriod === TimePeriod.MONTH
        }
        onClick={onClick}
        {...props}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <DateRangeIcon fontSize="inherit" />
          <Typography sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {label ?? t('TimePeriod.custom')}
          </Typography>
        </Stack>
      </ToggleButton>
    </Tooltip>
  )
}

export function DateRangeSelectToolbox({
  value,
  includeOneHourOption = false,
  includeThreeDaysOption = false,
  showAbsoluteTimePeriodOptions = true,
  showRelativeTimePeriodOptions = true,
  size = 'medium',
  customControl
}: DateRangeSelectToolboxProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The time period.
   */
  const { timePeriod } = useSelector((state: RootState) => ({
    timePeriod: state.query.timePeriod
  }))

  /**
   * The custom date range.
   */
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    from: value.from,
    to: value.to
  })

  /**
   * The difference of date range in minutes.
   */
  const differenceOfDateRangeInMinutes = Math.abs(differenceInMinutes(value.from, value.to))

  /**
   * Indicates if the time period is absolute.
   */
  const isAbsoluteTimePeriod =
    timePeriod === TimePeriod.DAY ||
    timePeriod === TimePeriod.WEEK ||
    timePeriod === TimePeriod.MONTH

  // Runs when the component mounts.
  useEffect(() => {
    dispatch(getInitialTimePeriod())
  }, [])

  // Runs when the value changes.
  useEffect(() => {
    setCustomDateRange({
      from: value.from,
      to: value.to
    })
  }, [value])

  /**
   * Handle the change event for selected time period.
   */
  function handleSelectedTimePeriodChange(
    _event: React.MouseEvent<HTMLElement>,
    selectedTimePeriod: TimePeriod
  ): void {
    const now = startOfMinute(new Date())

    if (selectedTimePeriod !== TimePeriod.CUSTOM || !isAbsoluteTimePeriod) {
      dispatch(setTimePeriod(selectedTimePeriod))
    }

    switch (selectedTimePeriod) {
      case TimePeriod.ONE_HOUR:
        dispatch(
          setDateRange({
            from: subHours(now, 1),
            to: now
          })
        )
        break

      case TimePeriod.TWENTYFOUR_HOURS:
        dispatch(
          setDateRange({
            from: subHours(now, 24),
            to: now
          })
        )
        break

      case TimePeriod.THREE_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 3),
            to: now
          })
        )
        break

      case TimePeriod.SEVEN_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 7),
            to: now
          })
        )
        break

      case TimePeriod.THIRTY_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 30),
            to: now
          })
        )
        break
    }
  }

  /**
   * Handle the change event for absolute time period.
   */
  function handleAbsoluteTimePeriodChange(
    _event: React.MouseEvent<HTMLElement>,
    selectedTimePeriod: TimePeriod
  ): void {
    // const now = new Date()
    const { from } = customDateRange
    dispatch(setTimePeriod(selectedTimePeriod))

    switch (selectedTimePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(from),
            to: endOfDay(from)
          })
        )
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(from, { locale: fi }),
            to: endOfWeek(from, { locale: fi })
          })
        )
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(from),
            to: endOfMonth(from)
          })
        )
        break
    }

    // Finally we can close the menu.
    // closeMenu()
  }

  /**
   * Change the start date of the custom date range.
   */
  function handleCustomDateRangeFromChange(from: Date | null): void {
    if (from !== null && isBefore(from, customDateRange.to)) {
      setTimePeriod(TimePeriod.CUSTOM)
      setCustomDateRange({
        ...customDateRange,
        from
      })

      if (!isAbsoluteTimePeriod) {
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
      }

      dispatch(
        setDateRange({
          ...customDateRange,
          from
        })
      )
    }
  }

  /**
   * Change the end date of the custom date range.
   */
  function handleCustomDateRangeToChange(to: Date | null): void {
    if (to !== null && isAfter(to, customDateRange.from)) {
      setTimePeriod(TimePeriod.CUSTOM)
      setCustomDateRange({
        ...customDateRange,
        to
      })

      if (!isAbsoluteTimePeriod) {
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
      }

      dispatch(
        setDateRange({
          ...customDateRange,
          to
        })
      )
    }
  }

  /**
   * Go backwards in time.
   */
  function goBackwards(): void {
    switch (timePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(subDays(value.from, 1)),
            to: endOfDay(subDays(value.to, 1))
          })
        )
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(subWeeks(value.from, 1), { locale: fi }),
            to: endOfWeek(subWeeks(value.to, 1), { locale: fi })
          })
        )
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(subMonths(value.from, 1)),
            to: endOfMonth(subMonths(value.to, 1))
          })
        )
        break

      default:
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
        dispatch(
          setDateRange({
            from: subMinutes(value.from, differenceOfDateRangeInMinutes),
            to: subMinutes(value.to, differenceOfDateRangeInMinutes)
          })
        )
    }
  }

  /**
   * Go forwards in time.
   */
  function goForwards(): void {
    switch (timePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(addDays(value.from, 1)),
            to: endOfDay(addDays(value.to, 1))
          })
        )
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(addWeeks(value.from, 1), { locale: fi }),
            to: endOfWeek(addWeeks(value.to, 1), { locale: fi })
          })
        )
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(addMonths(value.from, 1)),
            to: endOfMonth(addMonths(value.to, 1))
          })
        )
        break

      default:
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
        dispatch(
          setDateRange({
            from: addMinutes(value.from, differenceOfDateRangeInMinutes),
            to: addMinutes(value.to, differenceOfDateRangeInMinutes)
          })
        )
    }
  }

  return (
    <Box sx={{ mt: 2 }} onClick={(event) => event.stopPropagation()}>
      <Stack direction="column" spacing={6} alignItems="center" mb={6}>
        <Stack direction="row" spacing={0} alignItems="center" sx={{ width: '100%' }}>
          <DatePicker
            label={t('fromDate')}
            value={customDateRange.from}
            slotProps={{
              textField: {
                // size: 'small'
              }
            }}
            onChange={handleCustomDateRangeFromChange}
            sx={{
              width: '160px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }
            }}
            // @ts-expect-error typed incorrectly
            renderInput={(params) => <TextField {...params} />}
          />

          <TimePicker
            value={customDateRange.from}
            onChange={handleCustomDateRangeFromChange}
            slotProps={{
              textField: {
                // size: 'small'
              }
            }}
            sx={{
              width: '100px',
              flexShrink: 0,
              borderLeftRadius: 0,
              marginLeft: '-1px',
              outline: 0,
              '& .MuiOutlinedInput-notchedOutline': {
                borderLeftColor: 'transparent',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }
            }}
            // @ts-expect-error typed incorrectly
            renderInput={(params) => <TextField {...params} />}
          />
        </Stack>

        <Stack direction="row" spacing={0} alignItems="center" sx={{ width: '100%' }}>
          <DatePicker
            label={t('toDate')}
            value={customDateRange.to}
            slotProps={{
              textField: {
                // size: 'small'
              }
            }}
            sx={{
              width: '160px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }
            }}
            onChange={handleCustomDateRangeToChange}
            // @ts-expect-error typed incorrectly
            renderInput={(params) => <TextField {...params} />}
          />

          <TimePicker
            value={customDateRange.to}
            slotProps={{
              textField: {
                // size: 'small'
              }
            }}
            sx={{
              width: '100px',
              flexShrink: 0,
              borderLeftRadius: 0,
              marginLeft: '-1px',
              outline: 0,
              '& .MuiOutlinedInput-notchedOutline': {
                borderLeftColor: 'transparent',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0
              }
            }}
            onChange={handleCustomDateRangeToChange}
            // @ts-expect-error typed incorrectly
            renderInput={(params) => <TextField {...params} />}
          />
        </Stack>
      </Stack>

      <Grid container spacing={4}>
        {customControl && (
          <Grid item xs={12}>
            {customControl}
          </Grid>
        )}

        {showRelativeTimePeriodOptions && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  // color: theme.palette.text.primary,
                  // fontWeight: 600,
                  mb: 2
                }}
              >
                {t('relativeDateRanges')}
              </FormLabel>
              <RelativeTimePeriodToggleButtonGroup
                value={timePeriod}
                includeOneHourOption={includeOneHourOption}
                includeThreeDaysOption={includeThreeDaysOption}
                size={size}
                onChange={handleSelectedTimePeriodChange}
              />
            </FormControl>
          </Grid>
        )}

        {showAbsoluteTimePeriodOptions && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  // color: theme.palette.text.primary,
                  // fontWeight: 600,
                  mb: 2
                }}
              >
                {t('absoluteDateRanges')}
              </FormLabel>

              <AbosoluteTimePeriodToggleButtonGroup
                value={timePeriod}
                size={size}
                onChange={handleAbsoluteTimePeriodChange}
              />
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              color="inherit"
              sx={{ alignSelf: 'center' }}
              startIcon={<FastRewindIcon fontSize="inherit" />}
              onClick={goBackwards}
            >
              {t('goBackwards')}
            </Button>
            <Button
              variant="text"
              color="inherit"
              sx={{ alignSelf: 'center' }}
              endIcon={<FastForwardIcon fontSize="inherit" />}
              onClick={goForwards}
            >
              {t('goForwards')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/*<Stack direction="row" spacing={3} justifyContent="flex-end" mt={3}>
        <Button
          type="button"
          variant="text"
          color="secondary"
          onClick={handleCancel}
        >
          {t('cancel')}
        </Button>

        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleConfirm}
        >
          {t('confirm')}
        </Button>
      </Stack>*/}
    </Box>
  )
}

export interface DateRangeSelectProps extends DateRangeVisibilityOptions {
  value: DateRange
  includeCustomOption?: boolean
  marginTop?: number | string
  size?: 'small' | 'medium' | 'large' | undefined
  customControl?: ReactNode
  onChange?: {
    (value: DateRange): void
  }
}

export default function DateRangeSelect({
  value,
  includeOneHourOption = false,
  includeThreeDaysOption = false,
  includeCustomOption = true,
  showRelativeTimePeriodOptions = true,
  showAbsoluteTimePeriodOptions = true,
  marginTop = '0',
  size,
  customControl,
  onChange
}: DateRangeSelectProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The time period.
   */
  const { timePeriod } = useSelector((state: RootState) => ({
    timePeriod: state.query.timePeriod
  }))

  /**
   * The custom date range.
   */
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    from: value.from,
    to: value.to
  })

  /**
   * The difference of date range in minutes.
   */
  const differenceOfDateRangeInMinutes = Math.abs(differenceInMinutes(value.from, value.to))

  /**
   * The achor element for the menu.
   */
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  /**
   * Indicates if the menu is open.
   */
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)

  /**
   * Indicates if the time period is absolute.
   */
  const isAbsoluteTimePeriod =
    timePeriod === TimePeriod.DAY ||
    timePeriod === TimePeriod.WEEK ||
    timePeriod === TimePeriod.MONTH

  // Runs when the component mounts.
  useEffect(() => {
    dispatch(getInitialTimePeriod())
  }, [])

  // Runs when the value changes.
  useEffect(() => {
    setCustomDateRange({
      from: value.from,
      to: value.to
    })
  }, [value])

  /**
   * Open the menu.
   */
  function openMenu(): void {
    setMenuIsOpen(true)
  }

  /**>
   * Close the menu.
   */
  function closeMenu(): void {
    setMenuIsOpen(false)
  }

  /**
   * Handle the change event for selected time period.
   */
  function handleSelectedTimePeriodChange(
    _event: React.MouseEvent<HTMLElement>,
    selectedTimePeriod: TimePeriod
  ): void {
    const now = startOfMinute(new Date())

    if (selectedTimePeriod !== TimePeriod.CUSTOM || !isAbsoluteTimePeriod) {
      dispatch(setTimePeriod(selectedTimePeriod))
    }

    switch (selectedTimePeriod) {
      case TimePeriod.ONE_HOUR:
        dispatch(
          setDateRange({
            from: subHours(now, 1),
            to: now
          })
        )
        break

      case TimePeriod.TWENTYFOUR_HOURS:
        dispatch(
          setDateRange({
            from: subHours(now, 24),
            to: now
          })
        )
        break

      case TimePeriod.THREE_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 3),
            to: now
          })
        )
        break

      case TimePeriod.SEVEN_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 7),
            to: now
          })
        )
        break

      case TimePeriod.THIRTY_DAYS:
        dispatch(
          setDateRange({
            from: subDays(now, 30),
            to: now
          })
        )
        break
    }

    if (selectedTimePeriod === TimePeriod.CUSTOM) {
      openMenu()
    } else {
      closeMenu()
    }
  }

  /**
   * Handle the change event for absolute time period.
   */
  function handleAbsoluteTimePeriodChange(
    _event: React.MouseEvent<HTMLElement>,
    selectedTimePeriod: TimePeriod
  ): void {
    // const now = new Date()
    const from = customDateRange.from

    dispatch(setTimePeriod(selectedTimePeriod))

    switch (selectedTimePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(from),
            to: endOfDay(from)
          })
        )
        // setCustomDateRange({
        //   from: startOfDay(now),
        //   to: endOfDay(now)
        // })
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(from, { locale: fi }),
            to: endOfWeek(from, { locale: fi })
          })
        )
        // setCustomDateRange({
        //   from: startOfWeek(now, { locale: fi }),
        //   to: endOfWeek(now, { locale: fi })
        // })
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(from),
            to: endOfMonth(from)
          })
        )
        // setCustomDateRange({
        //   from: startOfMonth(now),
        //   to: endOfMonth(now)
        // })
        break
    }

    // Finally we can close the menu.
    // closeMenu()
  }

  /**
   * Change the start date of the custom date range.
   */
  function handleCustomDateRangeFromChange(from: Date | null): void {
    if (from !== null && isBefore(from, customDateRange.to)) {
      setTimePeriod(TimePeriod.CUSTOM)
      setCustomDateRange({
        ...customDateRange,
        from
      })
    }
  }

  /**
   * Change the end date of the custom date range.
   */
  function handleCustomDateRangeToChange(to: Date | null): void {
    if (to !== null && isAfter(to, customDateRange.from)) {
      setTimePeriod(TimePeriod.CUSTOM)
      setCustomDateRange({
        ...customDateRange,
        to
      })
    }
  }

  /**
   * Handle the open menu action.
   */
  function handleOpenMenu(event: React.MouseEvent<HTMLElement, MouseEvent>): void {
    setMenuAnchor(event.currentTarget)

    if (!isAbsoluteTimePeriod) {
      dispatch(setTimePeriod(TimePeriod.CUSTOM))
    }

    openMenu()
  }

  /**
   * Handle the cancel action.
   */
  function handleCancel(): void {
    closeMenu()
  }

  /**
   * Handle the confirm action.
   */
  function handleConfirm(): void {
    if (onChange !== undefined) {
      onChange(customDateRange)
    }

    if (!isAbsoluteTimePeriod) {
      dispatch(setTimePeriod(TimePeriod.CUSTOM))
    }

    dispatch(setDateRange(customDateRange))

    closeMenu()
  }

  /**
   * Go backwards in time.
   */
  function goBackwards(): void {
    switch (timePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(subDays(value.from, 1)),
            to: endOfDay(subDays(value.to, 1))
          })
        )
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(subWeeks(value.from, 1), { locale: fi }),
            to: endOfWeek(subWeeks(value.to, 1), { locale: fi })
          })
        )
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(subMonths(value.from, 1)),
            to: endOfMonth(subMonths(value.to, 1))
          })
        )
        break

      default:
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
        dispatch(
          setDateRange({
            from: subMinutes(value.from, differenceOfDateRangeInMinutes),
            to: subMinutes(value.to, differenceOfDateRangeInMinutes)
          })
        )
    }
  }

  /**
   * Go forwards in time.
   */
  function goForwards(): void {
    switch (timePeriod) {
      case TimePeriod.DAY:
        dispatch(
          setDateRange({
            from: startOfDay(addDays(value.from, 1)),
            to: endOfDay(addDays(value.to, 1))
          })
        )
        break

      case TimePeriod.WEEK:
        dispatch(
          setDateRange({
            from: startOfWeek(addWeeks(value.from, 1), { locale: fi }),
            to: endOfWeek(addWeeks(value.to, 1), { locale: fi })
          })
        )
        break

      case TimePeriod.MONTH:
        dispatch(
          setDateRange({
            from: startOfMonth(addMonths(value.from, 1)),
            to: endOfMonth(addMonths(value.to, 1))
          })
        )
        break

      default:
        dispatch(setTimePeriod(TimePeriod.CUSTOM))
        dispatch(
          setDateRange({
            from: addMinutes(value.from, differenceOfDateRangeInMinutes),
            to: addMinutes(value.to, differenceOfDateRangeInMinutes)
          })
        )
    }
  }

  return (
    <Stack direction="row" gap={2}>
      <Tooltip title={t('goBackwardsInTime') as string}>
        <IconButton size={size} sx={{ alignSelf: 'center' }} onClick={goBackwards}>
          <FastRewindIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      {showRelativeTimePeriodOptions ? (
        <RelativeTimePeriodToggleButtonGroup
          color="primary"
          value={timePeriod}
          includeOneHourOption={includeOneHourOption}
          includeThreeDaysOption={includeThreeDaysOption}
          onChange={handleSelectedTimePeriodChange}
          aria-label={t('Device.timePeriod')}
          size={size}
          sx={{
            marginTop,
            flexShrink: 0
          }}
        >
          {includeCustomOption && <CustomTimePeriodOption onClick={handleOpenMenu} />}
        </RelativeTimePeriodToggleButtonGroup>
      ) : showAbsoluteTimePeriodOptions ? (
        <AbosoluteTimePeriodToggleButtonGroup
          value={timePeriod}
          size={size}
          onChange={handleAbsoluteTimePeriodChange}
        >
          {includeCustomOption && <CustomTimePeriodOption onClick={handleOpenMenu} />}
        </AbosoluteTimePeriodToggleButtonGroup>
      ) : (
        <CustomTimePeriodOption label={t('selectDateRange')} onClick={handleOpenMenu} />
      )}

      <Menu
        open={menuIsOpen}
        anchorEl={menuAnchor}
        onClose={() => closeMenu()}
        elevation={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box onClick={(event) => event.stopPropagation()}>
          <Box px={6} py={3}>
            <Typography variant="h6" mb={6}>
              {t('selectDateRange')}
            </Typography>

            <Stack direction="column" spacing={6} alignItems="center" mb={6}>
              <Stack direction="row" spacing={3} alignItems="center">
                <DatePicker
                  label={t('fromDate')}
                  value={customDateRange.from}
                  onChange={handleCustomDateRangeFromChange}
                  // @ts-expect-error typed incorrectly
                  renderInput={(params) => <TextField {...params} sx={{ width: '130px' }} />}
                />

                <TimePicker
                  value={customDateRange.from}
                  onChange={handleCustomDateRangeFromChange}
                  // @ts-expect-error typed incorrectly
                  renderInput={(params) => <TextField {...params} sx={{ width: '100px' }} />}
                />
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center">
                <DatePicker
                  label={t('toDate')}
                  value={customDateRange.to}
                  onChange={handleCustomDateRangeToChange}
                  // @ts-expect-error typed incorrectly
                  renderInput={(params) => <TextField {...params} sx={{ width: '130px' }} />}
                />

                <TimePicker
                  value={customDateRange.to}
                  onChange={handleCustomDateRangeToChange}
                  // @ts-expect-error typed incorrectly
                  renderInput={(params) => <TextField {...params} sx={{ width: '100px' }} />}
                />
              </Stack>
            </Stack>

            {customControl && <Box sx={{ mb: 6 }}>{customControl}</Box>}

            <Grid container spacing={4}>
              {showRelativeTimePeriodOptions && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        // color: theme.palette.text.primary,
                        // fontWeight: 600,
                        mb: 2
                      }}
                    >
                      {t('relativeDateRanges')}
                    </FormLabel>
                    <RelativeTimePeriodToggleButtonGroup
                      value={timePeriod}
                      includeOneHourOption={includeOneHourOption}
                      includeThreeDaysOption={includeThreeDaysOption}
                      size={size}
                      onChange={handleSelectedTimePeriodChange}
                    />
                  </FormControl>
                </Grid>
              )}

              {showAbsoluteTimePeriodOptions && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        // color: theme.palette.text.primary,
                        // fontWeight: 600,
                        mb: 2
                      }}
                    >
                      {t('absoluteDateRanges')}
                    </FormLabel>

                    <AbosoluteTimePeriodToggleButtonGroup
                      value={timePeriod}
                      size={size}
                      onChange={handleAbsoluteTimePeriodChange}
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Stack direction="row" spacing={3} justifyContent="flex-end" mt={3}>
              <Button type="button" variant="text" color="secondary" onClick={handleCancel}>
                {t('cancel')}
              </Button>
              <Button type="button" variant="contained" color="primary" onClick={handleConfirm}>
                {t('confirm')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Menu>

      <Tooltip title={t('goForwardsInTime') as string}>
        <IconButton size={size} sx={{ alignSelf: 'center' }} onClick={goForwards}>
          <FastForwardIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
